import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import type { Page } from './types';

interface ApiQuery<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Small data-fetching hook: GET on mount + manual reload. Pass null to skip. */
export function useApiQuery<T>(path: string | null): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((d) => {
        if (alive.current) setData(d);
      })
      .catch((e: unknown) => {
        if (alive.current) setError(e instanceof ApiError ? e.message : 'Request failed');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

interface CursorList<T> {
  items: T[];
  error: string | null;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  reload: () => void;
  loadMore: () => void;
}

/**
 * Cursor-paginated list hook matching every m42 list endpoint's real
 * `{ items, nextCursor }` envelope (packages/core/src/pagination.ts's
 * toPage()). `basePath` must already include any `?` query string (filters,
 * `limit=`) — the hook appends `&cursor=` itself once a next page exists.
 */
export function useCursorList<T>(basePath: string | null): CursorList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(basePath));
  const [loadingMore, setLoadingMore] = useState(false);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!basePath) {
      setItems([]);
      setCursor(null);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<Page<T>>(basePath)
      .then((page) => {
        if (!alive.current) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setHasMore(Boolean(page.nextCursor));
      })
      .catch((e: unknown) => {
        if (alive.current) setError(e instanceof ApiError ? e.message : 'Request failed');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, tick]);

  const loadMore = useCallback(() => {
    if (!basePath || !cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    const sep = basePath.includes('?') ? '&' : '?';
    api
      .get<Page<T>>(`${basePath}${sep}cursor=${encodeURIComponent(cursor)}`)
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.nextCursor);
        setHasMore(Boolean(page.nextCursor));
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : 'Request failed');
      })
      .finally(() => setLoadingMore(false));
  }, [basePath, cursor, loadingMore]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { items, error, loading, loadingMore, hasMore, reload, loadMore };
}
